import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { useMessages } from '../../components/Messages';
import { getApiUrl, apiFetch } from '../../config/api';
import { showApiError } from '../../utils/errorUtils';
import './WarehouseDashboard.css';
import { formatAppDate } from '../../utils/dateUtils';

const LOCATION_TYPES = ['BULK', 'PALLET', 'SHELF', 'SECURE', 'QUARANTINE', 'STAGING'];

const ViewWarehouseLocation = forwardRef(({ locationId, onBack, editMode = false, createMode = false, onSave, onCancel, onEdit, onLocationCreated }, ref) => {
  const { t } = useTranslation('warehouse');
  const { t: tCommon } = useTranslation('common');
  const [location, setLocation] = useState(null);
  const [editData, setEditData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [inventory, setInventory] = useState(null);

  const { showSuccess, showError } = useMessages();

  useImperativeHandle(ref, () => ({
    handleDelete,
    handleSave
  }));

  useEffect(() => {
    if (locationId && !createMode) {
      fetchLocation();
      fetchInventory();
    } else if (createMode) {
      const newLocation = {
        location_code: '',
        location_name: '',
        zone: '',
        aisle: '',
        rack: '',
        bin: '',
        location_type: 'SHELF',
        max_capacity_cubic_meters: '',
        max_weight_kg: '',
        notes: '',
        is_active: true
      };
      setLocation(newLocation);
      setEditData(newLocation);
    }
  }, [locationId, createMode]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (editMode || createMode) {
          handleCancel();
        } else {
          if (onBack) onBack();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [editMode, createMode, onBack]);

  const fetchLocation = async () => {
    setLoading(true);
    try {
      const response = await apiFetch(getApiUrl(`/api/warehouse-locations/${locationId}`));
      if (response.ok) {
        const data = await response.json();
        setLocation(data);
        setEditData(data);
      } else {
        setError(t('warehouseLocations.error.fetchFailed'));
      }
    } catch (error) {
      console.error('Error fetching location:', error);
      setError(t('warehouseLocations.error.loadingError'));
    } finally {
      setLoading(false);
    }
  };

  const fetchInventory = async () => {
    try {
      const response = await apiFetch(getApiUrl(`/api/warehouse-locations/${locationId}/inventory`));
      setInventory(response.ok ? await response.json() : null);
    } catch (error) {
      console.error('Error fetching location inventory:', error);
      setInventory(null);
    }
  };

  const handleFieldChange = (field, value) => {
    setEditData(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      if (!editData.location_code || !editData.location_name) {
        showError(t('warehouseLocations.view.validation.required'));
        return;
      }

      const saveData = createMode
        ? {
            location_code: editData.location_code,
            location_name: editData.location_name || null,
            zone: editData.zone || null,
            aisle: editData.aisle || null,
            rack: editData.rack || null,
            bin: editData.bin || null,
            location_type: editData.location_type || 'SHELF',
            max_capacity_cubic_meters: editData.max_capacity_cubic_meters ? parseFloat(editData.max_capacity_cubic_meters) : null,
            max_weight_kg: editData.max_weight_kg ? parseFloat(editData.max_weight_kg) : null,
            notes: editData.notes || null
          }
        : {
            location_name: editData.location_name || null,
            zone: editData.zone || null,
            aisle: editData.aisle || null,
            rack: editData.rack || null,
            bin: editData.bin || null,
            location_type: editData.location_type || 'SHELF',
            max_capacity_cubic_meters: editData.max_capacity_cubic_meters ? parseFloat(editData.max_capacity_cubic_meters) : null,
            max_weight_kg: editData.max_weight_kg ? parseFloat(editData.max_weight_kg) : null,
            notes: editData.notes || null,
            is_active: editData.is_active
          };

      const url = createMode
        ? getApiUrl('/api/warehouse-locations')
        : getApiUrl(`/api/warehouse-locations/${locationId}`);

      const method = createMode ? 'POST' : 'PUT';

      const response = await apiFetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(saveData)
      });

      if (response.ok) {
        const updatedLocation = await response.json();
        setLocation(updatedLocation);
        setEditData(updatedLocation);
        setHasChanges(false);

        if (createMode) {
          showSuccess(t('warehouseLocations.view.messages.createdSuccess'));
          if (onLocationCreated) {
            onLocationCreated(updatedLocation.location_id);
          }
        } else {
          showSuccess(t('warehouseLocations.view.messages.updatedSuccess'));
        }

        if (onSave) {
          onSave();
        }
      } else {
        await showApiError(response, showError, tCommon);
      }
    } catch (error) {
      console.error('Error saving location:', error);
      showError(tCommon('errors.genericTitle'), t('warehouseLocations.view.messages.saveFailed'));
    }
  };

  const handleCancel = () => {
    if (hasChanges) {
      if (window.confirm(t('warehouseLocations.view.confirmations.unsavedChanges'))) {
        setEditData(location || {});
        setHasChanges(false);
        if (onCancel) onCancel();
      }
    } else {
      if (onCancel) onCancel();
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t('warehouseLocations.view.confirmations.deactivateConfirm', { name: location.location_name || location.location_code }))) {
      return;
    }

    try {
      const response = await apiFetch(getApiUrl(`/api/warehouse-locations/${locationId}`), {
        method: 'DELETE'
      });

      if (response.ok) {
        showSuccess(t('warehouseLocations.view.messages.deactivatedSuccess'));
        if (onBack) onBack();
      } else {
        await showApiError(response, showError, tCommon);
      }
    } catch (error) {
      console.error('Error deactivating location:', error);
      showError(tCommon('errors.genericTitle'), t('warehouseLocations.view.messages.deactivateFailed'));
    }
  };

  if (loading) {
    return <div className="view-container"><div className="loading">{t('warehouseLocations.loading')}</div></div>;
  }

  if (error && !location) {
    const errorMessage = typeof error === 'string' ? error : t('warehouseLocations.error.loadingError');
    return <div className="view-container"><div className="error-message">{errorMessage}</div></div>;
  }

  if (!location && !createMode) {
    return <div className="view-container"><div className="no-data">{t('warehouseLocations.view.titles.noLocationFound')}</div></div>;
  }

  return (
    <div className="view-container">
      <div className="view-header">
        <div className="header-title">
          <h2>
            {createMode ? t('warehouseLocations.view.titles.newLocation') : location?.location_name || t('warehouseLocations.view.titles.viewLocation')}
          </h2>
          <span className="header-id">{!createMode && location?.location_code}</span>
        </div>
        <div className="header-status">
          {editData.is_active ? (
            <span className="status-badge status-active">{t('common.status.active')}</span>
          ) : (
            <span className="status-badge status-inactive">{t('common.status.inactive')}</span>
          )}
        </div>
      </div>

      <div className="view-content">
        <div className="form-section">
          <h3>{t('warehouseLocations.view.sections.locationInfo')}</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>{t('warehouseLocations.view.fields.locationCode')} *</label>
              <input
                type="text"
                value={editData.location_code || ''}
                onChange={(e) => handleFieldChange('location_code', e.target.value.toUpperCase())}
                disabled={!createMode}
                required
                maxLength="50"
              />
            </div>
            <div className="form-group">
              <label>{t('warehouseLocations.view.fields.locationName')} *</label>
              <input
                type="text"
                value={editData.location_name || ''}
                onChange={(e) => handleFieldChange('location_name', e.target.value)}
                disabled={!editMode && !createMode}
                required
                maxLength="100"
              />
            </div>
            <div className="form-group">
              <label>{t('warehouseLocations.view.fields.locationType')}</label>
              <select
                value={editData.location_type || 'SHELF'}
                onChange={(e) => handleFieldChange('location_type', e.target.value)}
                disabled={!editMode && !createMode}
              >
                {LOCATION_TYPES.map(type => (
                  <option key={type} value={type}>{t(`warehouseLocations.view.types.${type}`)}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>{t('warehouseLocations.view.fields.status')}</label>
              <select
                value={editData.is_active ? 'active' : 'inactive'}
                onChange={(e) => handleFieldChange('is_active', e.target.value === 'active')}
                disabled={!editMode || createMode}
              >
                <option value="active">{t('common.status.active')}</option>
                <option value="inactive">{t('common.status.inactive')}</option>
              </select>
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3>{t('warehouseLocations.view.sections.physicalAddress')}</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>{t('warehouseLocations.view.fields.zone')}</label>
              <input
                type="text"
                value={editData.zone || ''}
                onChange={(e) => handleFieldChange('zone', e.target.value)}
                disabled={!editMode && !createMode}
                maxLength="50"
              />
            </div>
            <div className="form-group">
              <label>{t('warehouseLocations.view.fields.aisle')}</label>
              <input
                type="text"
                value={editData.aisle || ''}
                onChange={(e) => handleFieldChange('aisle', e.target.value)}
                disabled={!editMode && !createMode}
                maxLength="20"
              />
            </div>
            <div className="form-group">
              <label>{t('warehouseLocations.view.fields.rack')}</label>
              <input
                type="text"
                value={editData.rack || ''}
                onChange={(e) => handleFieldChange('rack', e.target.value)}
                disabled={!editMode && !createMode}
                maxLength="20"
              />
            </div>
            <div className="form-group">
              <label>{t('warehouseLocations.view.fields.bin')}</label>
              <input
                type="text"
                value={editData.bin || ''}
                onChange={(e) => handleFieldChange('bin', e.target.value)}
                disabled={!editMode && !createMode}
                maxLength="20"
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3>{t('warehouseLocations.view.sections.capacityLimits')}</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>{t('warehouseLocations.view.fields.maxCapacity')}</label>
              <input
                type="number"
                value={editData.max_capacity_cubic_meters || ''}
                onChange={(e) => handleFieldChange('max_capacity_cubic_meters', e.target.value)}
                disabled={!editMode && !createMode}
                min="0"
                step="0.01"
              />
            </div>
            <div className="form-group">
              <label>{t('warehouseLocations.view.fields.maxWeight')}</label>
              <input
                type="number"
                value={editData.max_weight_kg || ''}
                onChange={(e) => handleFieldChange('max_weight_kg', e.target.value)}
                disabled={!editMode && !createMode}
                min="0"
                step="0.01"
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <div className="form-grid">
            <div className="form-group full-width">
              <label>{t('warehouseLocations.view.fields.notes')}</label>
              <textarea
                value={editData.notes || ''}
                onChange={(e) => handleFieldChange('notes', e.target.value)}
                disabled={!editMode && !createMode}
                rows="3"
              />
            </div>
          </div>
        </div>

        {location && location.created_date && !createMode && (
          <div className="form-section">
            <h3>{t('warehouseLocations.view.sections.systemInfo')}</h3>
            <div className="info-grid">
              {location.current_utilization_percent != null && (
                <div className="info-item">
                  <label>{t('warehouseLocations.view.fields.utilization')}</label>
                  <span title={t('warehouseLocations.view.fields.utilizationHint')}>
                    {parseFloat(location.current_utilization_percent).toFixed(1)}%
                    {` (${Number(location.stored_weight_kg).toLocaleString()} / ${Number(location.max_weight_kg).toLocaleString()} kg)`}
                  </span>
                </div>
              )}
              <div className="info-item">
                <label>{t('warehouseLocations.view.fields.createdDate')}</label>
                <span>{formatAppDate(location.created_date, 'datetime')}</span>
              </div>
              {location.updated_date && (
                <div className="info-item">
                  <label>{t('warehouseLocations.view.fields.lastUpdated')}</label>
                  <span>{formatAppDate(location.updated_date, 'datetime')}</span>
                </div>
              )}
            </div>
            {location.current_utilization_percent != null && location.items_without_weight > 0 && (
              <div className="items-without-weight" style={{ marginTop: '0.75rem', color: 'var(--warning)' }}>
                ⚠️ {t('warehouseLocations.view.itemsWithoutWeight', { count: location.items_without_weight })}
                {inventory && (
                  <ul>
                    {inventory.items.filter(item => item.weight_kg == null).map(item => (
                      <li key={item.item_id}>{item.item_code} — {item.item_name} ({item.unit_of_measure || '-'})</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        )}

        {!createMode && inventory && (
          <div className="form-section">
            <h3>{t('warehouseLocations.view.sections.inventory')}</h3>
            {inventory.items.length === 0 ? (
              <div className="no-data">{t('warehouseLocations.view.inventoryEmpty')}</div>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{t('warehouseLocations.view.inventoryHeaders.itemCode')}</th>
                    <th>{t('warehouseLocations.view.inventoryHeaders.itemName')}</th>
                    <th style={{ textAlign: 'right' }}>{t('warehouseLocations.view.inventoryHeaders.available')}</th>
                    <th style={{ textAlign: 'right' }}>{t('warehouseLocations.view.inventoryHeaders.reserved')}</th>
                    <th style={{ textAlign: 'right' }}>{t('warehouseLocations.view.inventoryHeaders.total')}</th>
                    <th>{t('warehouseLocations.view.inventoryHeaders.unit')}</th>
                    <th style={{ textAlign: 'right' }}>{t('warehouseLocations.view.inventoryHeaders.weight')}</th>
                  </tr>
                </thead>
                <tbody>
                  {inventory.items.map(item => (
                    <tr key={item.item_id}>
                      <td>{item.item_code}</td>
                      <td>{item.item_name}</td>
                      <td style={{ textAlign: 'right' }}>{Number(item.available_quantity).toLocaleString()}</td>
                      <td style={{ textAlign: 'right' }}>{Number(item.reserved_quantity).toLocaleString()}</td>
                      <td style={{ textAlign: 'right' }}>{Number(item.total_quantity).toLocaleString()}</td>
                      <td>{item.unit_of_measure || '-'}</td>
                      <td style={{ textAlign: 'right' }}>
                        {item.weight_kg == null ? t('warehouseLocations.view.noWeight') : Number(item.weight_kg).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
});

export default ViewWarehouseLocation;
